// 1. React
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { VehicleOfferRentEditProps } from "./types";
import { OfferRentStatusHistory } from "services/api/fleet/vehicles/offers/types";
import { getOfferRentIsAvailableAPI, toggleOfferRentStatusHistoryAPI } from "services/api/fleet/vehicles/offers/api";
import { OfferRentStatusId } from "services/api/fleet/vehicles/offers/consts";

// 4. Hooks
import { useAvailability } from "services/api/hooks/useAvailability";

// 5. UI components
import { Modal, ModalIntro, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Tabs } from "ui/components/Tabs";
import { Loader } from "ui/components/Loader";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import successAnimation from "ui/theme/animations/success.json";

// 5. Local components
import VehicleOfferRentInfo from "./components/VehicleOfferRentInfo/VehicleOfferRentInfo";
import OfferStatusToolbar from "./components/VehicleOfferRentInfo/components/OfferHeader/OfferStatusToolbar";

// 6. Consts
import {
    FLEET_VEHICLE_OFFER_RENT_CREATE_PAGE_TITLE,
    FLEET_VEHICLE_OFFER_RENT_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";


const VehicleOfferRentEditCard: React.FC<VehicleOfferRentEditProps> = (props) => {
    const { show, vehicleId, offerId, onSuccess, onClose, onCreateContract } = props;

    const { t } = useTranslate();

    const [statusHistory, setStatusHistory] = useState<OfferRentStatusHistory[]>([]);
    const [currentOfferId, setCurrentOfferId] = useState<string | undefined>(offerId);
    const [currentStatusId, setCurrentStatusId] = useState<string | null>(null);

    const [showSuccessOverlay, setShowSuccessOverlay] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Intro animacija — prikazuje se uvijek (i za nove i za edit), gasi se tek kad se pojavi PDF
    const [showIntro, setShowIntro] = useState<boolean>(true);
    // PDF pregled — podignuto iz OfferRentInfo da parent može dohvatiti PDF pri otvaranju
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [showPdf, setShowPdf] = useState<boolean>(false);

    // Kreiranje vs ažuriranje ponude
    const isNewOffer = !offerId;

    // Provjera preduvjeta (postavke, podaci o tvrtki i stanje vozila) — samo za nove ponude
    const checkAvailability = useCallback(
        () => getOfferRentIsAvailableAPI(vehicleId),
        [vehicleId]
    );

    const { isAvailable, renderUnavailable } = useAvailability({
        apiFn: checkAvailability,
        enabled: show && isNewOffer,
    });

    // Forma se prikazuje kad nije loading i preduvjeti su ispunjeni (ili je edit mode)
    const showForm = !isLoading && (isAvailable || !isNewOffer);

    // Reset state kad se modal otvori ili zatvori
    useEffect(() => {
        setStatusHistory([]);
        setCurrentOfferId(offerId);
        setCurrentStatusId(null);
        setShowSuccessOverlay(false);
        setShowIntro(true);
        setPdfUrl("");
        setShowPdf(false);
        setIsLoading(!!offerId);
    }, [show, offerId]);

    // Zatvaranje success overlaya nakon kreiranja ponude
    const handleSuccessDismiss = () => {
        setShowSuccessOverlay(false);
    };

    // Sinkronizacija statusa iz child komponente (VehicleOfferRentInfo)
    const handleStatusHistoryChange = (history: OfferRentStatusHistory[], offerIdFromChild?: string, statusId?: string | null) => {
        setStatusHistory(history);
        if (offerIdFromChild) setCurrentOfferId(offerIdFromChild);
        if (statusId !== undefined) setCurrentStatusId(statusId ?? null);
    };

    // Promjena statusa ponude (npr. Poslano, Prihvaćeno, Plaćeno)
    const handleStatusToggle = async (statusId: string, checked: boolean) => {
        if (!currentOfferId) return;

        try {
            const result = await toggleOfferRentStatusHistoryAPI(currentOfferId, statusId, checked);
            setStatusHistory(result.statusHistory ?? []);
            setCurrentStatusId(result.status?.id ?? null);
        } catch {
            // error handled by snackbar
        }
    };

    return (
        <>
            <Modal
                show={show}
                onHide={onClose}
                position="side"
                size="medium"
                intro={showIntro ? {
                    content: (
                        <ModalIntro
                            animation={invoiceAnimation}
                            title={t("admin.fleet.vehicle.offer.rent.intro.title")}
                            description={t("admin.fleet.vehicle.offer.rent.intro.description")}
                            show={showIntro}
                        />
                    ),
                } : undefined}
            >
                <ModalHeader
                    icon="/media/images/add-icon.svg"
                    title={
                        offerId
                            ? FLEET_VEHICLE_OFFER_RENT_EDIT_PAGE_TITLE.text
                            : FLEET_VEHICLE_OFFER_RENT_CREATE_PAGE_TITLE.text
                    }
                    onHide={onClose}
                />
                <ModalBody>
                    {isLoading && (
                        <div className="relative h-[calc(100vh-80px)]">
                            <Loader isLoading={true}>{null}</Loader>
                        </div>
                    )}
                    {renderUnavailable({ title: t("admin.fleet.vehicle.offer.rent.unavailable.title"), onClose })}
                    <form noValidate className={showForm ? "" : "hidden"}>
                        <div className="px-4 mt-6">
                            <OfferStatusToolbar
                                statusHistory={statusHistory}
                                onToggle={handleStatusToggle}
                                disabled={currentStatusId === OfferRentStatusId.Cancelled}
                            />
                        </div>
                        <div className="flex mt-4 mb-6 px-4 min-h-[70vh] xl:min-h-[1000px]">
                            <Tabs
                                tabs={[
                                    {
                                        label: t("admin.fleet.vehicle.offer.rent.add.tab.info"),
                                        content: <div className="px-4 pt-3 pb-2 border-x border-b border-slate-200 rounded-b-md">
                                            <VehicleOfferRentInfo
                                                vehicleId={vehicleId}
                                                offerId={offerId}
                                                onStatusHistoryChange={handleStatusHistoryChange}
                                                currentStatusId={currentStatusId}
                                                onFinish={() => setShowSuccessOverlay(true)}
                                                onCreateContract={onCreateContract}
                                                setIsLoading={setIsLoading}
                                                onClose={onClose}
                                                hasIntro={showIntro}
                                                pdfUrl={pdfUrl}
                                                setPdfUrl={setPdfUrl}
                                                showPdf={showPdf}
                                                setShowPdf={setShowPdf}
                                            />
                                        </div>,
                                    },
                                ]}
                            />
                        </div>
                    </form>
                </ModalBody>
            </Modal>
            <SuccessOverlay
                show={showSuccessOverlay}
                animation={successAnimation}
                title={t("admin.fleet.vehicle.offer.rent.create.success.title")}
                description={t("admin.fleet.vehicle.offer.rent.create.success.description")}
                onDismiss={handleSuccessDismiss}
            />
        </>
    );
};
export default memo(VehicleOfferRentEditCard);
